import * as THREE from 'three';
import { CONFIG } from '../config.js';
import { Loop } from './Loop.js';
import { World } from './World.js';
import { Input } from '../input/Input.js';
import { Chaser } from '../entities/Chaser.js';
import { CameraRig } from '../render/CameraRig.js';
import { Environment } from '../render/Environment.js';
import { Particles } from '../render/Particles.js';
import { Quality } from '../render/Quality.js';
import { Sound } from '../audio/Sound.js';
import { pitUnder } from '../systems/Collision.js';
import { Hud } from '../ui/Hud.js';
import { Tutorial } from '../ui/Tutorial.js';
import { RecordMarker } from '../render/RecordMarker.js';
import { Screens } from '../ui/Screens.js';
import { Records } from '../storage/Records.js';
import { Wardrobe, SKINS } from '../storage/Wardrobe.js';
import { Missions, emptyStats } from '../storage/Missions.js';
import { laneNeeds, GEM_VALUE } from '../world/Generator.js';
import { DebugHud } from '../debug/DebugHud.js';
import { Autopilot } from '../debug/Autopilot.js';
import { CameraControl } from '../control/CameraControl.js';
import { GUIDANCE_TEXT } from '../pose/Gestures.js';
import { BodyNav } from '../ui/BodyNav.js';
import { Settings } from '../storage/Settings.js';
import { setDifficulty } from '../systems/Speed.js';

const END_MESSAGES = {
  fell: ['¡Te has caído!', 'Desliza hacia el lado de la curva en los últimos metros antes de la esquina.'],
  fellSide: ['¡Te has salido del camino!', 'Desde un carril exterior no se puede ir más hacia fuera.'],
  pit: ['¡Has caído en un foso!', 'Salta los fosos o cámbiate de carril antes de llegar a ellos.'],
  crash: ['¡Te has chocado!', 'Deslízate bajo las ramas y esquiva las rocas cambiando de carril.'],
  caught: ['¡El monstruo te ha atrapado!', 'Tras un tropiezo, aguanta unos segundos sin volver a fallar.'],
};
const COUNTDOWN = 3;
const GESTURE_FLASH = { jump: 'SALTO', crouch: 'AGACHADO', left: '← IZQUIERDA', right: 'DERECHA →' };
const LOST_HINT = 'No te veo: vuelve delante de la cámara y la partida seguirá sola.';
const SPRINT_FOV = 12; // grados extra de campo de visión durante el sprint
const PU_COLORS = { magnet: 0xb04cff, shield: 0x4cd8ff, sprint: 0xff8a1c };
const DEBRIS = { low: 0x8a5a2b, high: 0x6a8a3a, wall: 0x9aa3ad, deep: 0x9aa3ad };
const _v = new THREE.Vector3();

/** Vibración donde exista (Android); iOS Safari no la permite a las webs. */
function vibrate(pattern) {
  if (navigator.userActivation?.hasBeenActive) navigator.vibrate?.(pattern);
}

/**
 * Orquesta la partida: render, entrada, interfaz, sonido y estados del juego.
 *
 *   'menu'      el piloto automático juega de fondo detrás del menú
 *   'playing'   partida en curso
 *   'paused'    pausa (botón, Esc o al pasar la app a segundo plano)
 *   'countdown' 3-2-1 al reanudar
 *   'ending'    animación de muerte antes de la pantalla final
 *   'over'      pantalla de fin de partida
 */
export class Game {
  constructor(canvas) {
    const params = new URLSearchParams(location.search);
    this.fixedSeed = params.has('seed') ? Number(params.get('seed')) : null;
    this.settings = new Settings();
    setDifficulty(this.settings.difficulty);

    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, powerPreference: 'high-performance' });
    this.scene = new THREE.Scene();
    this.env = new Environment(this.scene);
    this.quality = new Quality(this.renderer, this.env);

    this.camera = new THREE.PerspectiveCamera(CONFIG.camera.fov, 1, 0.1, 220);
    this.baseFov = CONFIG.camera.fov;
    this.world = new World(this.scene);
    this.chaser = new Chaser(this.scene);
    this.particles = new Particles(this.scene);
    this.recordMarker = new RecordMarker(this.scene);
    this.tutorial = new Tutorial();
    this.rig = new CameraRig(this.camera);
    this.sound = new Sound();
    this.records = new Records();
    this.wardrobe = new Wardrobe();
    this.missions = new Missions();
    this.stats = emptyStats();
    this.player.model.applySkin(this.wardrobe.skin);
    this.hud = new Hud(document.getElementById('hud'), document.getElementById('flash'));
    this.screens = new Screens((a) => this.onUiAction(a));
    this.screens.setSound(this.sound.enabled, this.sound.musicOn);
    // Control por cámara: gestos en la partida y brazos para manejar los menús.
    this.nav = new BodyNav(document.getElementById('nav-hint'));
    this.screens.onShow = (el) => this.nav.setScreen(el);
    this.cam = new CameraControl({ video: document.getElementById('cam-video'), onGesture: (g) => this.#onCameraGesture(g) });
    this.dock = document.getElementById('cam-dock');
    this.dockCanvas = this.dock.querySelector('canvas');
    this.dockStatus = this.dock.querySelector('[data-field=dockStatus]');
    this.camCanvas = document.querySelector('[data-field=camCanvas]');
    this.pauseReason = null;
    this.camFlash = null; // último gesto leído, para mostrarlo un instante
    this.debug = new DebugHud(document.getElementById('debug'), this.renderer);
    this.demoPilot = new Autopilot();
    this.testPilot = params.has('autopilot') ? new Autopilot() : null;
    this.world.god = params.get('god') === '1'; // solo pruebas: invencible y gira solo
    this.input = new Input(window, (a) => this.onAction(a));
    this.loop = new Loop((dt) => this.frame(dt));

    this.state = null;
    this.stateTime = 0;
    this.time = 0;
    this.endReason = null;
    this.watch = { mult: 1, coins: 0, grounded: true, sliding: false, lane: 0, effects: { magnet: 0, shield: 0, sprint: 0 } };
    this.onGround = { player: true, monster: true };

    window.addEventListener('resize', () => this.resize());
    // El audio solo puede arrancar dentro de un gesto del usuario (sobre todo en iOS).
    for (const type of ['pointerdown', 'pointerup', 'touchend', 'keydown']) {
      window.addEventListener(type, () => this.sound.unlock(), { capture: true, passive: true });
    }
    // Al cambiar de app o bloquear el móvil, la partida se pausa sola y el sonido se detiene.
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && (this.state === 'playing' || this.state === 'countdown')) this.pause();
      this.sound.setHidden(document.hidden);
      this.quality.reset();
    });
    // iOS puede perder el contexto WebGL tras mucho tiempo en segundo plano: se recarga la página.
    canvas.addEventListener('webglcontextlost', (e) => e.preventDefault());
    canvas.addEventListener('webglcontextrestored', () => location.reload());
    // En el PC, cambiar de ventana (Alt+Tab) también pausa.
    window.addEventListener('blur', () => {
      if (this.state === 'playing') this.pause();
    });
    this.#applyControl();
    this.resize();
    this.enterMenu();
    // Si la última vez se jugó con la cámara, se ofrece directamente (un toque la enciende: así no se
    // enciende por sorpresa y el sonido puede arrancar con ese toque).
    if (this.settings.camera) this.screens.show('camera');
    this.#precompile();
  }

  /**
   * Compila de antemano los shaders de todo (también lo que está oculto: escudo, partículas…)
   * para que no haya tirones la primera vez que aparece algo en la partida.
   */
  #precompile() {
    const hidden = [];
    this.scene.traverse((o) => {
      if (!o.visible) {
        hidden.push(o);
        o.visible = true;
      }
    });
    this.renderer.compile(this.scene, this.camera);
    for (const o of hidden) o.visible = false;
  }

  get player() {
    return this.world.player;
  }

  start() {
    this.loop.start();
  }

  resize() {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.renderer.setSize(w, h);
    this.camera.aspect = w / h;
    // En vertical se abre el campo de visión para ver las curvas a tiempo.
    this.baseFov = w < h ? CONFIG.camera.fovPortrait : CONFIG.camera.fov;
    this.camera.fov = this.baseFov;
    this.camera.updateProjectionMatrix();
  }

  // ---------- Entrada ----------

  /** Gestos y teclado. */
  onAction(action) {
    if (action === 'mute') return this.toggleSound();
    switch (this.state) {
      case 'menu':
        if (action === 'confirm') this.startRun();
        return;
      case 'over':
        if (action === 'confirm') this.startRun();
        return;
      case 'paused':
        if (action === 'confirm' || action === 'pause') this.resume();
        return;
      case 'playing':
        break;
      default:
        return;
    }
    const p = this.player;
    switch (action) {
      case 'left':
        p.requestLateral(-1);
        break;
      case 'right':
        p.requestLateral(1);
        break;
      case 'up':
      case 'confirm':
        p.requestJump();
        break;
      case 'down':
        p.requestSlide();
        break;
      case 'pause':
        this.pause();
        break;
    }
  }

  /** Botones de la interfaz. */
  onUiAction(action) {
    this.sound.unlock();
    this.sound.play('click');
    if (action.startsWith('skin:')) return this.#chooseSkin(action.slice(5));
    if (this.#cameraUiAction(action)) return;
    // Abandonar una partida desde la pausa también la registra: las monedas no se pierden.
    if ((action === 'restart' || action === 'menu') && (this.state === 'paused' || this.state === 'countdown')) {
      const w = this.world;
      this.records.submit({ score: w.score, distance: w.player.distance, coins: w.coins });
    }
    switch (action) {
      case 'play':
      case 'retry':
      case 'restart':
        this.startRun();
        break;
      case 'menu':
        this.enterMenu();
        break;
      case 'resume':
        this.resume();
        break;
      case 'pause':
        if (this.state === 'playing') this.pause();
        break;
      case 'help':
        this.screens.toggleHelp();
        break;
      case 'wardrobe':
        this.screens.renderWardrobe(SKINS, this.wardrobe, this.bank);
        this.screens.toggleWardrobe();
        break;
      case 'sound':
        this.toggleSound();
        break;
      case 'music':
        this.sound.setMusicOn(!this.sound.musicOn);
        this.screens.setSound(this.sound.enabled, this.sound.musicOn);
        break;
      case 'continue':
        this.continueRun();
        break;
    }
  }

  // ---------- Control por cámara ----------

  /** Botones de la cámara y de los ajustes. Devuelve true si la acción era suya. */
  #cameraUiAction(action) {
    switch (action) {
      case 'camera':
        this.screens.show('camera');
        return true;
      case 'cam-start':
        this.#startCamera();
        return true;
      case 'cam-off':
        this.cam.stop();
        this.settings.set({ camera: false });
        this.#applyControl();
        this.screens.showMenu(this.records, this.missions.list());
        return true;
      case 'calibrate':
        // En la pantalla de la cámara se vuelven a medir también tus gestos; en la pausa, solo quieto.
        this.cam.recalibrate({ train: this.screens.current === 'camera' });
        return true;
      case 'cam-skip':
        this.cam.skipTraining();
        return true;
      case 'preview':
        this.settings.set({ preview: !this.settings.preview });
        this.#applyControl();
        return true;
      case 'difficulty':
        this.settings.set({ difficulty: this.settings.difficulty === 'soft' ? 'normal' : 'soft' });
        setDifficulty(this.settings.difficulty);
        this.#applyControl();
        return true;
    }
    return false;
  }

  async #startCamera() {
    // La primera vez que se juega con la cámara se propone la dificultad suave (se puede cambiar).
    if (!this.settings.camera && !this.settings.cameraRuns && this.settings.difficulty === 'normal') {
      this.settings.set({ difficulty: 'soft' });
      setDifficulty('soft');
    }
    this.settings.set({ camera: true });
    const started = this.cam.start({ profile: this.settings.gestureProfile }).catch(() => {}); // el error se muestra en la pantalla de la cámara
    this.#applyControl();
    await started;
    this.#applyControl();
  }

  /** Ajustes que dependen del control: ventana de giro, textos de los botones. */
  #applyControl() {
    const on = this.cam.running || this.cam.status === 'loading';
    this.player.turnAssist = on ? CONFIG.turn.cameraAssist : 1;
    this.screens.setOptions({ camera: on, difficulty: this.settings.difficulty, preview: this.settings.preview });
  }

  /** Gestos de la cámara (llegan fuera del bucle de render, en cuanto se detectan). */
  #onCameraGesture(type) {
    const playing = this.state === 'playing';
    const p = this.player;
    switch (type) {
      case 'calibrated':
        if (this.cam.detector.trained) this.settings.set({ gestureProfile: this.cam.detector.profile });
        this.sound.play('powerUp', 'shield');
        this.nav.setActive(true);
        if (this.screens.current === 'camera') {
          this.#refreshCameraScreen();
          this.nav.focusDefault();
        }
        break;
      case 'rep':
        this.sound.play('coin');
        break;
      case 'step':
        this.sound.play('beep');
        break;
      case 'lost':
        if (playing || this.state === 'countdown') this.pause('lost');
        break;
      case 'found':
        if (this.state === 'paused' && this.pauseReason === 'lost') this.resume();
        break;
    }
    if (GESTURE_FLASH[type]) this.camFlash = { text: GESTURE_FLASH[type], until: performance.now() + 600 };
    switch (type) {
      case 'jump':
        if (playing) p.requestJump();
        break;
      case 'crouch':
        if (playing) p.requestSlide();
        break;
      case 'left':
      case 'right':
        if (playing) p.requestSide(type === 'left' ? -1 : 1); // carril, o giro si hay una curva cerca
        else if (type === 'left' ? this.nav.next() : this.nav.select()) {
          if (type === 'left') this.sound.play('lane');
        }
        break;
    }
  }

  /** Pantalla de la cámara: avisos de colocación, calibración y botones. */
  #refreshCameraScreen() {
    const cam = this.cam;
    const d = cam.detector;
    const on = cam.status === 'on';
    const placed = d.guidance === 'ok' || d.guidance === 'feet';
    const train = d.trainingStatus;
    const progress = d.calib ? d.calib.progress : train ? train.progress : d.calibrated ? 1 : 0;
    const flash = this.#flashText();
    let guidance;
    if (cam.status === 'loading') guidance = 'Cargando el detector de movimiento…';
    else if (!on) guidance = 'Pulsa «Activar cámara»: el navegador te pedirá permiso.';
    else if (d.calib) guidance = !placed ? GUIDANCE_TEXT[d.guidance] : progress > 0 ? `Quieto… ${Math.ceil(3 - progress * 3)}` : 'Quédate quieto, de cuerpo entero';
    else if (!d.visible) guidance = GUIDANCE_TEXT[d.guidance];
    else if (train) guidance = `${train.prompt}${train.reps > 1 ? ` (${train.rep + 1} de ${train.reps})` : ''}`;
    else if (flash) guidance = `Leído: ${flash}`;
    else guidance = '¡Listo! Brazo derecho: elegir · brazo izquierdo: cambiar de botón';
    const info = train
      ? 'Calibrando tus gestos: hazlos como en el juego, con naturalidad. Se guardan para la próxima vez.'
      : 'Colócate a 2–3 m con buena luz. Prueba los gestos: aquí verás cuál lee.';
    const cpu = cam.tracker?.info.backend === 'wasm' ? ' Sin aceleración gráfica: va por la CPU (actívala en los ajustes del navegador).' : '';
    this.screens.updateCamera({
      status: cam.status,
      calibrated: d.calibrated,
      training: !!train,
      progress,
      guidance,
      error: cam.status === 'error' ? cam.error : '',
      info: on ? `${info}${cpu}` : '',
      overlay: cam.status === 'loading' ? 'Cargando…' : on ? '' : 'Cámara apagada',
    });
  }

  /** Imagen de la cámara: en su pantalla y, si se quiere, la miniatura inferior. */
  #drawCamera() {
    const cam = this.cam;
    this.nav.setActive(cam.ready);
    const camScreen = this.screens.current === 'camera';
    if (camScreen) {
      this.#refreshCameraScreen();
      if (cam.running) {
        this.camCanvas.style.aspectRatio = `${cam.aspect}`;
        cam.draw(this.camCanvas);
      }
    }
    const dock = cam.running && this.settings.preview && !camScreen;
    if (this.dock.hidden === dock) {
      this.dock.hidden = !dock;
      document.body.classList.toggle('cam-dock-on', dock);
    }
    if (!dock) return;
    this.dockCanvas.style.aspectRatio = `${cam.aspect}`;
    cam.draw(this.dockCanvas);
    const d = cam.detector;
    const flash = this.#flashText();
    const status = !d.visible ? 'No te veo' : d.calib ? 'Calibrando: quieto…' : d.trainingStatus ? d.trainingStatus.prompt : flash ?? '';
    if (this.dockStatus.textContent !== status) this.dockStatus.textContent = status;
    this.dockStatus.classList.toggle('flash', !!flash && status === flash);
  }

  /** Gesto leído hace menos de 0,6 s (para enseñar qué ha entendido la cámara), o null. */
  #flashText() {
    return this.camFlash && performance.now() < this.camFlash.until ? this.camFlash.text : null;
  }

  /** Pulsación en un traje del vestuario: equiparlo o comprarlo. */
  #chooseSkin(id) {
    const result = this.wardrobe.choose(id, this.earned);
    if (result === 'poor') {
      this.sound.play('powerDown');
      return;
    }
    if (result === 'bought') this.sound.play('record');
    this.player.model.applySkin(this.wardrobe.skin);
    this.screens.renderWardrobe(SKINS, this.wardrobe, this.bank);
  }

  /** Monedas ganadas en total (partidas + misiones) y las que quedan para gastar. */
  get earned() {
    return this.records.data.totalCoins + this.missions.earned;
  }

  get bank() {
    return this.wardrobe.balance(this.earned);
  }

  toggleSound() {
    this.sound.setEnabled(!this.sound.enabled);
    this.screens.setSound(this.sound.enabled, this.sound.musicOn);
  }

  // ---------- Estados ----------

  setState(state) {
    this.state = state;
    this.stateTime = 0;
  }

  #newSeed() {
    return this.fixedSeed ?? Math.floor(Math.random() * 2 ** 31);
  }

  #resetWorld(options) {
    this.world.reset(this.#newSeed(), options);
    this.player.start();
    this.rig.reset(this.player);
    this.particles.clear();
    const p = this.player;
    Object.assign(this.watch, { mult: 1, coins: 0, grounded: true, sliding: false, lane: p.lane });
    Object.assign(this.watch.effects, this.world.effects);
  }

  /** Menú con una partida de demostración de fondo. */
  enterMenu() {
    this.#resetWorld();
    this.recordMarker.reset(0);
    this.hud.hide();
    this.screens.setCountdown(0);
    this.screens.showMenu(this.records, this.missions.list());
    this.sound.setMusic('menu');
    this.setState('menu');
  }

  startRun() {
    // El monstruo arranca pegado al jugador y se va quedando atrás.
    this.#resetWorld({ startGap: CONFIG.chaser.startGap });
    this.recordMarker.reset(this.records.data.bestDistance >= 50 ? this.records.data.bestDistance : 0);
    const camera = this.cam.running;
    this.tutorial.start(camera ? this.settings.cameraRuns : this.records.data.runs, camera);
    if (camera) this.settings.set({ cameraRuns: this.settings.cameraRuns + 1 });
    this.missions.startRun();
    this.continues = 0;
    this.stats = emptyStats();
    this.passedRows = new WeakSet();
    this.screens.hide();
    this.screens.setCountdown(0);
    this.hud.update(this.world);
    this.hud.show();
    this.sound.setMusic('run');
    this.sound.play('growl');
    this.setState('playing');
  }

  /** reason: 'lost' si es porque has salido de la cámara (se reanuda sola al volver). */
  pause(reason = null) {
    this.pauseReason = reason;
    this.screens.setCountdown(0);
    this.screens.showPause(reason === 'lost' ? LOST_HINT : '');
    this.sound.setMusic(null);
    this.setState('paused');
  }

  resume() {
    this.pauseReason = null;
    this.screens.hide();
    this.lastBeep = 0;
    this.setState('countdown');
  }

  gameOver() {
    const w = this.world;
    const score = w.score;
    const isNew = this.records.submit({ score, distance: w.player.distance, coins: w.coins });
    const [title, hint] = END_MESSAGES[this.endReason];
    this.hud.hide();
    this.screens.showOver({
      title,
      hint,
      score,
      distance: Math.floor(w.player.distance),
      coins: w.coins,
      best: this.records.data.bestScore,
      isNew,
      bank: this.bank,
      missions: this.missions.completedThisRun,
      continueCost: this.#canContinue() ? this.#continueCost() : 0,
    });
    this.sound.play(isNew ? 'record' : 'gameOver');
    this.setState('over');
  }

  #continueCost() {
    return CONFIG.revive.cost * 2 ** this.continues;
  }

  #canContinue() {
    return this.continues < CONFIG.revive.max && this.bank >= this.#continueCost();
  }

  /** Segunda oportunidad: paga, deshace el registro de la partida y reaparece tras una cuenta atrás. */
  continueRun() {
    if (this.state !== 'over' || !this.#canContinue()) return;
    const cost = this.#continueCost();
    this.records.undoLast();
    this.wardrobe.spend(cost);
    this.continues++;
    this.world.revive();
    this.rig.reset(this.player);
    this.particles.clear();
    const p = this.player;
    Object.assign(this.watch, { grounded: true, sliding: false, lane: p.lane });
    this.screens.hide();
    this.hud.update(this.world);
    this.hud.show();
    this.sound.play('powerUp', 'shield');
    this.lastBeep = 0;
    this.setState('countdown');
  }

  // ---------- Bucle ----------

  frame(dt) {
    const t0 = performance.now();
    this.stateTime += dt;
    if (this.state === 'countdown') {
      const left = Math.ceil(COUNTDOWN - this.stateTime);
      this.screens.setCountdown(left > 0 ? left : 0);
      if (left !== this.lastBeep) {
        this.lastBeep = left;
        this.sound.play('beep', left <= 0);
      }
      if (left <= 0) {
        this.sound.setMusic('run');
        this.setState('playing');
      }
    }

    const simulating = this.state === 'playing' || this.state === 'ending' || this.state === 'menu';
    if (simulating) {
      // Física en subpasos fijos pequeños: sin atravesar obstáculos aunque baje el fps.
      const steps = Math.max(1, Math.ceil(dt / CONFIG.sim.step));
      const h = dt / steps;
      for (let i = 0; i < steps; i++) this.step(h);
      this.time += dt;
      if (this.state === 'playing') this.#watchPlayer();
    }

    const w = this.world;
    const p = this.player;
    const animDt = simulating ? dt : 0;
    w.path.animate(this.time);
    p.setEffects(w.effects, this.time, w.grace);
    p.syncMesh(animDt);
    this.chaser.update(w, animDt);
    this.rig.update(p, animDt);
    this.#updateFov(dt);
    this.onGround.player = p.state !== 'falling' && !pitUnder(p);
    this.env.update(animDt, this.time, this.camera, p, this.chaser, this.onGround);
    this.particles.update(animDt);
    this.sound.intensity = Math.min(1, (p.speed - CONFIG.speed.base) / (CONFIG.speed.max - CONFIG.speed.base));
    this.sound.danger = this.state === 'playing' && w.chaser.near > 0;
    this.renderer.render(this.scene, this.camera);
    if (this.state === 'playing') {
      this.hud.update(w);
      if (this.recordMarker.update(p, w.path, this.time)) this.#recordPassed();
    }
    this.#drawCamera();
    if (simulating) this.quality.update(dt);
    this.debug.update(dt, this, performance.now() - t0);
  }

  step(dt) {
    const pilot = this.state === 'menu' ? this.demoPilot : this.state === 'playing' ? this.testPilot : null;
    pilot?.update(this.player);
    const event = this.world.step(dt);

    if (this.state === 'menu') {
      // La demo no termina nunca: si muere, empieza otra.
      if (World.FATAL.has(event)) this.#resetWorld();
      return;
    }
    if (event) this.#onEvent(event);
    if (this.state === 'playing' && World.FATAL.has(event)) {
      this.endReason = event;
      this.hud.hurt();
      this.hud.update(this.world); // quita el aviso de peligro (el jugador ya no corre)
      this.sound.setMusic(null);
      this.setState('ending');
    }
    if (this.state === 'ending' && this.stateTime >= CONFIG.endDelay) this.gameOver();
  }

  /** Sonido, partículas y sacudidas de los eventos de la simulación. */
  #onEvent(event) {
    const p = this.player;
    const w = this.world;
    switch (event) {
      case 'turn':
        this.sound.play('turn');
        this.stats.turns++;
        break;
      case 'stumble':
        this.hud.hurt();
        this.sound.play('stumble');
        this.rig.shake(0.35);
        this.#debris(p.lastHit, 10);
        vibrate(60);
        break;
      case 'smash':
        this.sound.play('smash');
        this.rig.shake(0.2);
        this.#debris(p.lastHit, 22);
        break;
      case 'powerUp':
        this.stats.powerUps++;
        this.hud.announce(w.lastPowerUp);
        this.sound.play('powerUp', w.lastPowerUp);
        this.particles.burst(_v.copy(p.position).setY(p.y + 1.1), {
          count: 28,
          color: PU_COLORS[w.lastPowerUp],
          speed: 5,
          up: 1,
          life: 0.7,
          gravity: -2,
        });
        break;
      case 'crash':
        this.sound.play('crash');
        this.rig.shake(0.6);
        this.#debris(p.lastHit, 14);
        vibrate([80, 40, 120]);
        break;
      case 'caught':
        this.sound.play('caught');
        this.rig.shake(0.7);
        vibrate([80, 40, 160]);
        break;
      case 'fell':
      case 'fellSide':
      case 'pit':
        this.sound.play('fall');
        vibrate(120);
        break;
    }
  }

  /** Has superado la distancia de tu mejor partida. */
  #recordPassed() {
    const p = this.player;
    this.hud.announce(null, '¡Récord superado!', 'gold');
    this.sound.play('powerUp', 'sprint');
    this.particles.burst(_v.copy(p.position).setY(p.y + 2.5), { count: 40, color: 0xffd23a, speed: 6, up: 2, life: 0.9, gravity: -6 });
  }

  /** Astillas o lascas de piedra en el obstáculo golpeado. */
  #debris(o, count) {
    if (!o) return;
    const p = this.player;
    const x = Math.max(o.xMin, Math.min(o.xMax, p.x));
    const s = Math.max(o.s - o.depth / 2, Math.min(o.s + o.depth / 2, p.s + 0.4));
    this.world.path.worldPoint(p.segment, s, x, _v);
    _v.y = Math.max(o.bottom, Math.min(o.top, p.y + 0.8));
    this.particles.burst(_v, { count, color: DEBRIS[o.type] ?? 0xffffff, speed: 5, up: 2, life: 0.8, gravity: -14 });
  }

  /** Cambios del jugador que no son eventos de la simulación: monedas, saltos, carriles, efectos. */
  #watchPlayer() {
    const p = this.player;
    const w = this.world;
    const watch = this.watch;
    if (w.coins - watch.coins >= GEM_VALUE) {
      this.sound.play('gem');
      this.particles.burst(_v.copy(p.position).setY(p.y + 1), { count: 16, color: 0xff3355, speed: 3.5, up: 2, life: 0.6, gravity: -3 });
    } else if (w.coins > watch.coins) {
      this.sound.play('coin');
      this.particles.burst(_v.copy(p.position).setY(p.y + 1), { count: 6, color: 0xffd23a, speed: 2.5, up: 1.5, life: 0.4, gravity: -3 });
    }
    watch.coins = w.coins;
    const mult = w.multiplier;
    if (mult > watch.mult) {
      this.hud.announce(null, `¡Racha x${mult}!`, 'gold');
      this.sound.play('powerUp', 'shield');
    }
    watch.mult = mult;
    const hint = this.tutorial.update(p);
    if (hint) this.hud.hint(hint);
    this.#updateMissions();
    if (p.state === 'running') {
      if (watch.grounded && !p.grounded && p.vy > 0) this.sound.play('jump');
      if (!watch.grounded && p.grounded) {
        this.sound.play('land');
        this.particles.burst(_v.copy(p.position).setY(0.1), { count: 6, color: 0x6f6a5e, speed: 1.5, up: 0.5, life: 0.45, gravity: -1 });
      }
      if (!watch.sliding && p.sliding && p.grounded) this.sound.play('slide');
      if (p.lane !== watch.lane) this.sound.play('lane');
    }
    // Pisadas: una por zancada mientras corre por el suelo.
    const stride = Math.floor(p.model.phase / Math.PI);
    if (p.grounded && !p.sliding && stride !== watch.stride) this.sound.play('step');
    watch.stride = stride;
    watch.grounded = p.grounded;
    watch.sliding = p.sliding;
    watch.lane = p.lane;
    for (const k in w.effects) {
      if (watch.effects[k] > 0 && w.effects[k] === 0) this.sound.play('powerDown');
      watch.effects[k] = w.effects[k];
    }
  }

  /** Contadores de la partida para las misiones y aviso al cumplir una. */
  #updateMissions() {
    const p = this.player;
    const w = this.world;
    const st = this.stats;
    st.coins = w.coins;
    st.distance = p.distance;
    st.streak = Math.max(st.streak, w.multiplier);
    // Filas superadas limpiamente en su carril: troncos saltados, zarzas por debajo, fosos saltados.
    if (p.state === 'running') {
      for (const row of p.segment.rows) {
        if (row.end > p.s - 0.5) break;
        if (this.passedRows.has(row)) continue;
        this.passedRows.add(row);
        const need = laneNeeds(row.pieces)[p.lane];
        const o = p.segment.obstacles.find((q) => Math.abs(q.s - row.s) < 0.5 && q.xMin < p.x && p.x < q.xMax);
        if (need === 'low' && o && !o.broken && !o.hit) st.logs++;
        else if (need === 'high' && o && !o.broken) st.brambles++;
        else if (need === 'pit') st.pits++;
      }
    }
    for (const m of this.missions.check(st)) {
      this.hud.announce(null, `¡Misión cumplida! +${m.reward} ●`, 'gold');
      this.sound.play('record');
    }
  }

  #updateFov(dt) {
    const target = this.baseFov + (this.world.effects.sprint > 0 ? SPRINT_FOV : 0);
    if (Math.abs(this.camera.fov - target) < 0.05) return;
    this.camera.fov += (target - this.camera.fov) * (1 - Math.exp(-6 * dt));
    this.camera.updateProjectionMatrix();
  }
}
